<template>
    <info-widget>
        <template v-slot:title>
            Direct Deposit Information
        </template>
        <template v-slot:headerbutton>
            <button v-if="accounts.length && !readonly && !invalidDirectDeposit" class="btn btn-outline-primary" type="button" @click.prevent="openEditAccountsModal">
                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                Edit
            </button>
        </template>
        <slot>
            <div class="card-body py-2">
                <!-- Empty state -->
                <div v-if="!accounts.length">
                    <div class="row align-items-center mb-hf">
                        <div class="col-9">
                            <p class="mb-0">
                                <em>No payment accounts added.</em>
                            </p>
                        </div>
                        <div class="col-3 text-right">
                            <more-info class="mt-0">
                                Click "Add Account" if you wish to be paid using direct deposit. Otherwise, you will be paid by check.
                            </more-info>
                        </div>
                    </div>
                    <div class="card mb-1" v-if="!readonly && !invalidDirectDeposit">
                        <div class="card-body p-0 text-center">
                            <a class="d-block p-2" href @click.prevent="openAddAccountsModal()">
                                <i class="far fa-fw fa-plus mr-1"></i>
                                Add Account
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Invalid state -->
                <alert-box v-if="invalidDirectDeposit" type="caution" class="mb-2">
                    {{ invalidDirectDepositMessage }}
                </alert-box>

                <!-- Populated state -->
                <template v-if="accounts.length">
                <div v-for="(account, idx) in accounts" :key="account.id" class="d-flex align-items-start mb-2">
                    <div v-if="totalDisplayCount > 1" class="dd-account-number-circle flex-shrink-0">{{ idx + 1 }}</div>
                    <div class="flex-grow-1">
                        <direct-deposit-account-card
                            :account="account"
                            :numAccounts="totalDisplayCount"
                            :approvalView="approvalView"
                            :invalidDirectDeposit="invalidDirectDeposit"
                            :url="url"
                        >
                        </direct-deposit-account-card>
                    </div>
                </div>
                <div v-if="!aosClient && !hasRemainderAccount" class="d-flex align-items-start mb-2">
                    <div v-if="totalDisplayCount > 1" class="dd-account-number-circle flex-shrink-0">{{ accounts.length + 1 }}</div>
                    <div class="flex-grow-1">
                        <direct-deposit-account-card
                            :paperCheck="true"
                            :numAccounts="totalDisplayCount"
                            :approvalView="approvalView"
                            :url="url"
                        >
                        </direct-deposit-account-card>
                    </div>
                </div>
                </template>
            </div>
        </slot>

        <direct-deposit-modal
            :direct-deposit-accounts="directDepositAccounts"
            :mode="modalMode"
            v-if="isModalOpen"
            :url="url"
            :approvalView="approvalView"
            :essView="essView"
            :mandatoryDirectDeposit="mandatoryDirectDeposit"
            :canOverrideMandatoryDirectDeposit="canOverrideMandatoryDirectDeposit"
            :aosClient="aosClient"
            @updated="onAccountsUpdated($event)"
            @close="isModalOpen = false"
        />
    </info-widget>
</template>

<script>
import DirectDepositModal from '@/components/DirectDepositModal'
import DirectDepositAccountCard from '@/components/DirectDepositAccountCard'
import InfoWidget from '@/components/InfoWidget'

export default {
    components: {DirectDepositModal, DirectDepositAccountCard, InfoWidget, },
    props: {
        directDepositAccounts: null,
        url: null,
        readonly: null,
        approvalView: null,
        essView: null,
        hasInvalidDirectDeposit: null,
        mandatoryDirectDeposit: null,
        canOverrideMandatoryDirectDeposit: null,
        aosClient: {default: true},
    },
    computed: {
        accounts() {
            // Remainder is always last; non-remainder accounts ordered by sequence_number (highest first).
            const accounts = this.directDepositAccounts.slice()
            return accounts.sort((a, b) => {
                const aIsRemainder = a.split_type == 'remainder'
                const bIsRemainder = b.split_type == 'remainder'
                if (aIsRemainder !== bIsRemainder) return aIsRemainder ? 1 : -1
                return (b.sequence_number ?? 0) - (a.sequence_number ?? 0)
            })
        },
        hasRemainderAccount() {
            return this.accounts.some(a => a.split_type == 'remainder')
        },
        totalDisplayCount() {
            // Total cards rendered, including the synthetic paper-check card when there's no remainder (non-AOS only).
            return this.accounts.length + (!this.aosClient && !this.hasRemainderAccount ? 1 : 0)
        },
        totalPercentage() {
            return this.accounts
                .filter(a => a.split_type == 'percentage')
                .reduce((sum, a) => sum + (a.percentage || 0), 0)
        },
        hasMixedSplitTypes() {
            return this.accounts.some(a => a.split_type == 'percentage')
                && this.accounts.some(a => a.split_type == 'fixed')
        },
        hasInvalidPercentageTotal() {
            // Non-AOS clients may route the remainder to a paper check, so missing remainder is valid for them.
            if (this.totalPercentage > 100) return true
            if (this.aosClient && this.totalPercentage < 100 && !this.hasRemainderAccount) return true
            return false
        },
        invalidDirectDeposit() {
            if (this.accounts.length == 0) return false
            return this.hasMixedSplitTypes || this.hasInvalidPercentageTotal
        },
        invalidDirectDepositMessage() {
            if (this.hasMixedSplitTypes) {
                return 'Your direct deposit preferences are locked for editing because they include a mix of percentage and fixed-amount accounts. To make them editable, contact your manager to update your bank accounts. Otherwise, your direct deposits will continue as normal.'
            }
            return 'Your direct deposit preferences are locked for editing because the deposit percentages do not add up to 100%. To make them editable, contact your manager to change deposit percentages for your bank accounts. Otherwise, your direct deposits will continue as normal.'
        },
    },
    data() {
        return {
            isModalOpen: false,
            modalMode: '',
        }
    },
    methods: {
        openEditAccountsModal() {
            this.modalMode = 'edit'
            this.isModalOpen = true
        },
        openAddAccountsModal() {
            this.modalMode = 'add'
            this.isModalOpen = true
        },
        onAccountsUpdated() {
            this.$emit('updated')
        },
    },
}
</script>

<style scoped>
.dd-account-number-circle {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: #e9ecef;
    color: #495057;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    margin-top: 0.75rem;
    margin-right: 0.5rem;
}
</style>
